import React, { useState, useEffect, useContext, useRef } from 'react';
import html2canvas from 'html2canvas'; // Import the rendering library
import jsPDF from 'jspdf';           // Import the PDF generating library
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm , useFieldArray } from 'react-hook-form';
import { Layout } from '../../../../components';
import AppContext from '../../../../contexts/AppContext';
import { handleApi, formatCurrency, formatTwoDecimal, formatMediumDate, capitalize, AudioSpinner,useToast } from '../../../../utils/utils';



// View component for the filterable product input

const ProductSearchInput = ({ products, onSelectProduct, selectedProduct }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef(null);

  // Add a safeguard to ensure 'products' is a valid array before filtering
  const filteredProducts = (Array.isArray(products) ? products : []).filter(product => {
    if (!product || typeof product.label !== 'string') {
      return false; // Skip any non-valid product objects
    }
    return product.label.toLowerCase().includes(searchTerm.toLowerCase());
  });
  

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  // FINAL FIX: Add a check to ensure selectedProduct is a valid object before trying to access its properties.
  useEffect(() => {
    if (selectedProduct && typeof selectedProduct.product_name === 'string') {
      setSearchTerm(selectedProduct.product_name);
    } else {
      setSearchTerm('');
    }
  }, [selectedProduct]);

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <input
        type="text"
        className="w-full bg-gray-500 text-white border-b-2 focus:outline-none focus:border-cyan-300 border-cyan-200 rounded-md p-1 pl-2"
        placeholder="Search product..."
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setShowDropdown(true);
        }}
        onFocus={() => setShowDropdown(true)}
      />
      {showDropdown && (
        <ul className="absolute z-10 w-full mt-1 bg-gray-700 rounded-md shadow-lg max-h-48 overflow-y-auto">
          {filteredProducts.length > 0 ? (
            filteredProducts.map((product) => (
              <li
                key={product.value}
                className="p-2 text-white hover:bg-cyan-200 hover:text-gray-800 cursor-pointer"
                onClick={() => {
                  onSelectProduct(product);
                  setSearchTerm(product.label);
                  setShowDropdown(false);
                }}
              >
                {product.label}
              </li>
            ))
          ) : (
            <li className="p-2 text-gray-400">No products found.</li>
          )}
        </ul>
      )}
    </div>
  );
};


// Icons for the buttons (using simple inline SVG for elegance and no extra dependencies)
const DownloadIcon = (props) => (
  <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const PrintIcon = (props) => (
  <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m0 0v2a2 2 0 002 2h4a2 2 0 002-2v-2M9 17h6" />
  </svg>
);

const EmailIcon = (props) => (
  <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const PrintableQuotation = React.forwardRef(({ initialQuotation = initialQuotation, companyProfile = companyProfile, getStatusStyles: getStatusStylesProp = getStatusStyles, formatCurrency: formatCurrencyProp = formatCurrency, formatMediumDate: formatMediumDateProp = formatMediumDate }, ref) => {
    
  // Use destructured props or fallbacks
  const qData = initialQuotation;
  const cData = companyProfile;
  const formatCurrency = formatCurrencyProp;
  const formatMediumDate = formatMediumDateProp;

  // Determine the title based on the reference status
  const isSalesOrder = !!qData?.reference && qData.state === 'Confirmed';
  const documentTitle = isSalesOrder ? 'SALES ORDER' : 'QUOTATION';
  const status = qData?.state;
  const { text: statusText } = getStatusStylesProp(status);

  // Finalized Terms & Conditions (Minimal and Clear)
  const termsAndConditions = [
      "1. All prices are in MK and are valid for 30 days.",
      "2. Payment is due upon receipt of the invoice.",
      "3. Delivery times may vary based on product availability."
  ];


  return (
      // High-contrast, max-width 5xl for a spacious feel, and crisp sans-serif font
      <div ref={ref} className="p-10 mx-auto max-w-5xl bg-white text-gray-900 shadow-none font-sans text-sm">
          
          {/* COMPANY LETTERHEAD (Header) - Minimalist Cyan Line */}
          <div className="flex justify-between items-end border-b-2 border-cyan-500 pb-6 mt-4">
              {/* Logo and Company Details */}
              <div className="flex items-start">
                  <img 
                      src={cData.photo_url} 
                      alt={`${cData.name} Logo`} 
                      className="h-20 w-20 mr-4 object-contain" 
                      onError={(e) => { e.target.style.display = 'none' }}
                  />
                  <div className="text-left pt-1">
                
                    <h1 className="text-4xl font-bold text-gray-800 tracking-widest text-center text-capitalize">{cData.name.toUpperCase()}</h1> {/* Option 1 Style */}


                  <p className="text-sm text-cyan-600 font-semibold mt-1">{cData.tagline}</p>
                  </div>
              </div>

              {/* Company Contact Info - Right-aligned and legible */}
              <div className="text-right text-xs pt-2 text-gray-600">
                  <p>{cData.physical_address}, {cData.city}, {cData.country}</p>
                  <p>{cData.postal_address}</p>
                  <p className="font-medium mt-1">Email: {cData.email}</p>
                  <p className="font-bold">Phone: {cData.phone_number}</p>
              </div>
          </div>

          {/* DOCUMENT TITLE AND REFERENCE - Modern Title Block */}
          <div className="flex justify-between items-end border-b-2 border-cyan-500 pb-3 pb-8 mb-4">
              <div className="text-left">
                  <h2 className="text-4xl font-black uppercase text-cyan-600 font-semibold tracking-wider">
                      {documentTitle}
                  </h2>
              </div>
              <div className="text-right text-sm">
                  <p className="text-xl font-bold text-gray-800">{qData?.reference || qData?.id}</p>
                  <p className="text-xs text-gray-500">Date: {formatMediumDate(qData?.date) || 'N/A'} | Status: {statusText}</p>
              </div>
          </div>

          {/* BILLING AND INFO SECTION - Clean and Bounded */}
          <div className="grid grid-cols-2 gap-8 mb-8 text-sm">
              
              {/* Billed To */}
              <div className="pr-4 border-r-2 border-cyan-500">
                  <div className='bg-gray-800 text-cyan-400  rounded text-cyan-300 w-fit px-12  pb-2'> 
                    <p className="font-bold uppercase  ">Billed To</p>
                  </div>
                 
                  <p className="font-extrabold text-base">{qData?.partners?.name || 'N/A'}</p>
                  <p className="text-gray-700">{qData?.partners?.physical_address || 'Address Not Available'}</p>
                  <p className="mt-2 text-xs">Contact: {qData?.partners?.contact_person || 'N/A'}</p>
                  <p className="text-xs">Email: {qData?.partners?.email || 'N/A'}</p>
                  <p className="text-xs">Tel: {qData?.partners?.phone_number || 'N/A'}</p>
              </div>

              {/* Quotation Description */}
              <div className="pl-4">
                <div className='bg-gray-800 text-cyan-400  rounded text-cyan-300 w-fit px-12  pb-2'> 
                  <p className="font-bold uppercase  ">Notes</p>
                </div>
                 

                    <p className="whitespace-pre-wrap text-gray-700 text-sm italic">
                      {qData.description || 'No detailed description provided for this quotation.'}
                  </p>
              </div>
          </div>

          {/* TRANSACTION LINES (The Body) - Highly Differentiated Header */}
          <div className="border border-cyan-500/50 rounded-md overflow-hidden mb-8 shadow-md">
              {/* Table Header */}
              <div className="grid grid-cols-10 gap-2 px-4 py-3 bg-gray-800 text-cyan-400 font-extrabold uppercase text-xs">
                  <div className="col-span-4 text-left">Product / Description</div>
                  <div className="col-span-2 text-right">Unit Price</div>
                  <div className="col-span-1 text-center">Qty</div>
                  <div className="col-span-1 text-center">Disc %</div>
                  <div className="col-span-2 text-right"> Total (Excl.)</div>
              </div>
              
              {/* Table Body */}
              <div className="divide-y divide-cyan-100">
                  {qData?.transactionLines?.length > 0 ? (
                      qData?.transactionLines.map((field, index) => (
                          <div key={index} className="grid grid-cols-10 items-center px-4 py-2 text-xs">
                              <div className="col-span-4 text-left pr-2 font-medium text-gray-800">{field.description || 'N/A'}</div>
                              <div className="col-span-2 text-right text-gray-600">{formatTwoDecimal(field.unitPriceExclusive)}</div>
                              <div className="col-span-1 text-center font-bold">{field?.quantity}</div>
                              <div className="col-span-1 text-center text-red-700">{field?.discount_percentage}%</div>
                              <div className="col-span-2 text-right font-extrabold text-gray-900">
                                  {formatCurrency(field.lineDiscountedPriceExclusive)}
                              </div>
                          </div>
                      ))
                  ) : (
                      <p className="p-4 text-center text-gray-500 text-sm">No transaction lines on this quotation.</p>
                  )}
              </div>
          </div>

          {/* SUMMARY & TOTALS / TERMS & CONDITIONS */}
          <div className="flex justify-between items-start">
              
              {/* Terms and Conditions Section (Minimized) */}
              <div className="w-1/2 pr-12">
                <div className='bg-gray-800 text-cyan-400  rounded text-cyan-300 w-fit px-4  pb-2'> 
                  <p className="font-bold uppercase  ">  Terms & Conditions</p>
                </div>
                  
                  {/* Using list-none for a cleaner numbered look as the numbers are already in the text */}
                  <ul className="list-none pl-0 text-sm space-y-1 text-gray-700">
                      {termsAndConditions.map((term, index) => (
                          <li key={index} className="pl-0">{term}</li>
                      ))}
                  </ul>
              </div>
              
              {/* Totals Calculation */}
              <div className="w-full max-w-xs">
                  <div className="space-y-2 text-sm text-gray-700">
                      {/* Subtotal */}
                      <div className="flex justify-between py-1">
                          <div>Subtotal (Excl. Tax):</div>
                          <div className="font-semibold text-gray-800">{formatCurrency(qData.totalDiscountedPriceExclusive)}</div>
                      </div>
                      {/* Discount */}
                      {/* <div className="flex justify-between py-1">
                          <div>Total Discount:</div>
                          <div className="font-semibold text-red-700">- {formatCurrency(qData.totalDiscountExclusive)}</div>
                      </div> */}
                      {/* Tax */}
                      <div className="flex justify-between pt-2 border-t border-cyan-500/50">
                          <div>Total Tax:</div>
                          <div className="font-semibold text-gray-800">{formatCurrency(qData.totalTaxAfterDiscounted)}</div>
                      </div>
                  </div>
                  {/* Grand Total - BOLD CYAN BOX */}
                 
                  <div className='flex justify-between bg-gray-800 text-cyan-400  rounded font-extrabold text-xl text-cyan-300 mt-2 px-2  pb-2'> 
                    <div>GRAND TOTAL:</div>
                    <div>{formatCurrency(qData.totalDiscountedPriceInclusive)}</div>
                </div>
                  
                  {/* DISCOUNT*/}
                  <div className="flex justify-between pt-1 mt-4 px-4 py-2 text-cyan-600 font-extrabold text-sm">
                      <div>TOTAL DISCOUNT:</div>
                      <div>{formatCurrency(qData.totalDiscountInclusive)}</div>
                  </div>
              </div>
          </div>

          {/* Footer / Signature Area */}
          <div className="mt-20 pt-8 text-center text-xs text-gray-600">
          
          <hr class="w-2/3 mx-auto border-0 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent my-6" />

<p className="mb-8 italic">We sincerely appreciate you considering us as your partner.<br />
We are excited by the potential of this collaboration and are poised to deliver.</p>
 {/* <div className="flex justify-around">
                  <div className="w-1/3">
                      <div className="pb-1 border-b-2 border-gray-600 mb-1"></div>
                      <p className="text-xs font-bold text-gray-800 tracking-wider">Prepared By (Signature)</p>
                  </div>
                  <div className="w-1/3">
                      <div className="pb-1 border-b-2 border-gray-600 mb-1"></div>
                      <p className="text-xs font-bold text-gray-800 tracking-wider">Client Acceptance (Signature & Stamp)</p>
                  </div>
              </div> */}
          </div>

      </div>
  );
});
// You would typically export the main component for use in the parent file
// export default PrintableQuotation; 
const getStatusStyles = (status) => {
  let text = status || 'Draft';
  let pillClasses = 'bg-gray-200 text-gray-700';
  return { text, pillClasses };
};

const NewInvoice = () => {
  const location = useLocation()
  const navigate = useNavigate();
  const { initialQuotation } = location.state || {};
  console.log('intial qoue',initialQuotation)
  
//   const [quotation, setQuotation] = useState(initialFormState);
 const [quotation, setQuotation] = useState(initialQuotation || {}); 
  const [transactionLines, setTransactionLines] = useState([]);
  const [products, setProducts] = useState([]);
  const [formattedProducts, setFormattedProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [formattedCustomers, setFormattedCustomers] = useState([]);
  const [selectedCustomerDetails, setSelectedCustomerDetails] = useState(null); //state to hold customer details
  const { userData, currentColor, actionCount,companyProfile,setActionCount} = useContext(AppContext);
  console.log('company Profile',companyProfile)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false); // NEW STATE
  const { showToast, toastMessage } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false); // State for the reset confirmation modal
  const [focusedIndex, setFocusedIndex] = useState(null);
  // const quotationRef = useRef(null); // Ref to the quotation content for PDF generation
  const quantityRefs = useRef([]);    // a ref for each quantity input field to manage focus
   
     // State to track if the generation was successful
     const [isInvoiceGenerated, setIsInvoiceGenerated] = useState(false);
 
  
  
  
    // const quotationRef = useRef(null); // Ref to the quotation content for PDF generation
   


  // const handleAction = (action) => {
  //   if (action === 'download') {
  //     showToast(`Simulated action: ${action}`, 'success');
  //   } else if (action === 'print') {window.print();
  //   } else {showToast(`Simulated action: ${action}`, 'success');}
  // };

  const TransactionLineHeader = () => {
    return (
      <div className="grid grid-cols-6 gap-2 p-2 border-b rounded-lg bg-gray-100 text-gray-800 font-semibold text-xs md:text-xs">
        <div className="col-span-2 text-left">Product Description</div>
        <div className="col-span-1 text-center">Price</div>
        <div className="col-span-1 text-center">Qty</div>
        <div className="col-span-1 text-center">Disc %</div>
        <div className="col-span-1 text-right">Total</div>
      </div>
    );
  };

  const getStatusStyles = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return {
          text: 'APPROVED',
          pillClasses: 'bg-emerald-700/50 text-emerald-300 ring-emerald-500/30',
        };
      case 'invoiced':
        return {
          text: 'INVOICED',
          pillClasses: 'bg-violet-700/50 text-violet-300 ring-violet-500/30',
        };
      case 'draft':
        return {
          text: 'DRAFT',
          pillClasses: 'bg-slate-700/50 text-slate-300 ring-slate-500/30',
        };
      case 'cancelled':
        return {
          text: 'CANCELLED',
          pillClasses: 'bg-rose-700/50 text-rose-300 ring-rose-500/30',
        };
      default:
        return {
          text: status?.toUpperCase() || 'UNKNOWN',
          pillClasses: 'bg-gray-700/50 text-gray-400 ring-gray-500/30',
        };
    }
  };
  
  // ... inside your component
  const status = initialQuotation?.state;
  const { text, pillClasses } = getStatusStyles(status);
 
  // Inside the NewInvoice component...

const quotationRef = useRef(null); // Ref to the quotation content for PDF generation
// ... (other state/refs)

const handleDownload = async () => {
  // 1. Trigger the rendering of the PrintableQuotation component off-screen
  setIsGeneratingPdf(true); 

  // Wait for the state update and render cycle to complete
  // Use a small delay to ensure the component is fully rendered in the DOM
  await new Promise(resolve => setTimeout(resolve, 50)); 
  
  const input = quotationRef.current; 
  
  if (!input) {
      // This should now be impossible, but acts as a safeguard
      setIsGeneratingPdf(false);
      showToast("Error: Printable quotation layout not rendered.", 'error');
      return;
  }

  setIsLoading(true);

  try {
      // 2. Perform the high-quality PDF generation
      const canvas = await html2canvas(input, {
          scale: 3, 
          useCORS: true, 
          logging: false,
          backgroundColor: '#ffffff', // Explicitly white background
      });

      const imgData = canvas.toDataURL('image/jpeg', 1.0); 
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      while (heightLeft >= 0) {
          let position = heightLeft - imgHeight;
          pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
          heightLeft -= pdf.internal.pageSize.getHeight();
          if (heightLeft >= 0) {
              pdf.addPage();
          }
      }

      const filename = `${initialQuotation.reference || 'Quotation'}-${initialQuotation.id || new Date().getTime()}.pdf`;
      pdf.save(filename);
      
      showToast("Formal quotation downloaded successfully!", 'success');

  } catch (error) {
      console.error("PDF generation failed:", error);
      showToast("Failed to generate PDF. Check console for details.", 'error');
  } finally {
      setIsLoading(false);
      // 3. Hide the component immediately after the download process is done
      setIsGeneratingPdf(false); 
  }
};

const handleAction = (action) => {
    if (action === 'download') {
        handleDownload(); // Call the new function!
    } else if (action === 'print') {
        window.print();
    } else {
        showToast(`Simulated action: ${action}`, 'success');
    }
};

const handleSubmit = async () => {
        console.log('here sir in handle submit ');
        if (isLoading || isInvoiceGenerated) return;
        setIsLoading(true);
        showToast('Creating  invoice...', 'info');
        const invoiceDate = new Date().toISOString().split('T')[0];
        const newInvoiceData = {
            date: new Date().toISOString().split('T')[0],
            state: 'draft',
            order_id: quotation?.id || 0,
        };
        console.log('invoie data sor' ,newInvoiceData)
        try {
            const response = await handleApi('sales/invoices', newInvoiceData, userData, 'POST', {});
            if (response.status === 200) {
              showToast('Invoice created  successfully !', 'success');
              setQuotation(prev => ({...prev, 
                  id: response.data.new_invoice_id || prev.id, state: 'draft', 
              }));
              setIsInvoiceGenerated(true);  // Set the flag to true to trigger label/title changes in the JSX
            
            } else {showToast('Quotation successfully converted to an invoice!', 'error');}
        } catch (error) {showToast('Failed to generate  invoice!', 'error');}
        finally {
            setActionCount(actionCount + 1); // Trigger a re-fetch in parent components if needed
            setIsLoading(false);
        }
    };
   return (
    <Layout>
      {/* 1. CONDITIONAL OFF-SCREEN RENDERING */}
      {isGeneratingPdf && (
          <div 
              // This is the key styling: renders the content way off-screen
              className="absolute -left-[9999px] -top-[9999px] opacity-0" 
              style={{ width: '800px' }} // Set a fixed width for consistent PDF output
          >
              <PrintableQuotation
                  ref={quotationRef} // Attach the ref here
                  initialQuotation={initialQuotation}
                  companyProfile={companyProfile} // Make sure COMPANY_PROFILE is defined
                  getStatusStyles={getStatusStyles}
                  formatCurrency={formatCurrency}
                  formatMediumDate={formatMediumDate}
              />
          </div>
      )}

      <div className="min-h-screen text-white p-2 sm:p-4">
        {/* Toast Message Display */}
        {toastMessage.show && (
          <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-lg shadow-md z-50
            ${toastMessage.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
            {toastMessage.message}
          </div>
        )}
        
        {/* Header and Back Button */}
        <div className="relative flex justify-between items-center mb-4">
        <h1 className="font-extrabold text-gray-800 tracking-wide flex-grow text-center text-xl sm:text-2xl">
  
   
    NEW INVOICE

</h1>
{/* Action Button Group (NEW) */}
<div className="flex gap-1 sm:gap-2 mr-2"> 

              <button
                onClick={() => handleSubmit()}
                className="px-4 py-1 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-500 transition-all duration-300 flex items-center gap-1 text-sm sm:text-base"
                title="Convert to Invoice"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="hidden sm:inline">Generate Invoice</span>
              </button>
          

             <button
              onClick={() => handleAction('download')}
              className="p-2 rounded-full text-cyan-300 bg-gray-700/50 hover:bg-cyan-500 hover:text-gray-900 transition-all duration-300 shadow-md border border-cyan-500/50"
              aria-label="Download Quotation"
              title="Download PDF"
            >
              <DownloadIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
            <button
              onClick={() => handleAction('print')}
              className="p-2 rounded-full text-cyan-300 bg-gray-700/50 hover:bg-cyan-500 hover:text-gray-900 transition-all duration-300 shadow-md border border-cyan-500/50"
              aria-label="Print Quotation"
              title="Print"
            >
              <PrintIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
            <button
              onClick={() => handleAction('email')}
              className="p-2 rounded-full text-cyan-300 bg-gray-700/50 hover:bg-cyan-500 hover:text-gray-900 transition-all duration-300 shadow-md border border-cyan-500/50"
              aria-label="Email Quotation"
              title="Email"
            >
              <EmailIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>


          <div className="flex gap-2">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full text-gray-800 bg-white hover:bg-gray-300 transition-all duration-300 shadow-md"
              aria-label="Go back"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
          </div>
        </div>

            {/* Main Content Area */}
            <div 
          className="bg-gray-800/90 backdrop-blur-sm rounded-3xl border border-cyan-700/50 shadow-2xl p-4 sm:p-6 mx-auto max-w-4xl"
          ref={quotationRef} // <--- THIS IS THE MISSING PIECE!
        > 
          {isLoading ? (
            <div className="flex flex-col items-center py-10">
              <AudioSpinner />
              <p className="mt-4 text-cyan-300 text-base">Loading Quotation Data. Please wait...</p>
            </div>
          ) : (
            <div className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                
                {/* Customer Info Card - View Only */}
                <div className="bg-gray-700/70 border border-cyan-500/30 p-4 rounded-xl shadow-lg">
                  <p className="font-extrabold text-cyan-200 text-center mb-3 uppercase text-lg border-b border-cyan-500/50 pb-2">Customer Details</p>
                  <div className="flex flex-col gap-3 text-sm">
                    {/* Customer Name (Replaces Select Field) */}
                    <div className="flex flex-col">
                      <label className="font-medium text-cyan-300 mb-1">Customer Name</label>
                      <div className="w-full bg-gray-700 text-white font-bold border-b border-cyan-400 py-1 px-1">
                        {initialQuotation?.partners?.name || 'N/A'}
                      </div>
                    </div>

                
                      <>
                        <div className="flex justify-between items-center">
                          <label className="font-medium text-cyan-300">Telephone</label>
                          <span className="text-white font-medium text-right">{initialQuotation?.partners?.phone_number || 'NA'}</span>
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <label className="font-medium text-cyan-300">Contact Person</label>
                          <span className="text-white font-medium text-right">{initialQuotation?.partners?.contact_person || 'NA'}</span>
                        </div>

                        <div className="flex justify-between items-center">
                          <label className="font-medium text-cyan-300">Email Address</label>
                          <span className="text-white font-medium text-right">{initialQuotation?.partners?.email || 'NA'}</span>
                        </div>
                      </>
                  
                  </div>
                </div>

                {/* Quotation Info Card - View Only */}
                <div className="bg-gray-700/70 border border-cyan-500/30 p-4 rounded-xl shadow-lg">
                <p className="font-extrabold text-cyan-200 text-center mb-3 uppercase text-lg border-b border-cyan-500/50 pb-2">
  {
    // Check if the reference exists (is truthy)
    initialQuotation.reference
      ? 'Sales Order Details'  // If YES (reference is available)
      : 'Quotation Details'    // If NO (reference is not available)
  }
</p>
                  <div className="flex flex-col gap-3 text-sm">
                    {/* Description (Replaces Textarea) */}
                    {initialQuotation.description ? (
  // If description exists, show the description
  <p className="w-full bg-gray-700 text-white border-b border-cyan-400 p-1 min-h-[40px] whitespace-pre-wrap">
    {initialQuotation.description}
  </p>
) : (
  // Otherwise, show the placeholder
  <div className="w-full bg-gray-700 text-white border-b border-cyan-400 p-1 min-h-[40px] whitespace-pre-wrap">
    {'No description provided.'}
  </div>
)}
                    
                    <div className="flex justify-between items-center">
                          <label className="font-medium text-cyan-300">Reference</label>
                          <span className="text-white font-medium text-right">{initialQuotation?.reference || initialQuotation?.id || 'NA'}</span>
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <label className="font-medium text-cyan-300">Date</label>
                          <span className="text-white font-medium text-right">{formatMediumDate(initialQuotation?.date) || 'NA'}</span>
                        </div>

                        <div className="flex justify-between items-center py-2 border-b border-gray-700/50">
  <label className="font-light text-sm tracking-wider text-cyan-300 uppercase">
    Status
  </label>
  
  <span 
    className={`
      inline-flex 
      items-center 
      px-3 py-1 
      text-xs 
      font-semibold 
      rounded-full 
      uppercase 
      tracking-widest 
      shadow-lg 
      ring-1
      transition-colors 
      duration-300
      ${pillClasses}
    `}
  >
    {text}
  </span>
</div>
                    
                  </div>
                </div>
              </div>

              {/* Transaction Lines Section - View Only */}
            
                <div className="bg-gray-700/70 border border-cyan-500/30 rounded-xl shadow-lg overflow-hidden">
                  {/* Table Header */}
                  <div className="grid grid-cols-10 gap-2 p-3 bg-cyan-900 text-cyan-200 font-extrabold uppercase text-xs sm:text-sm md:text-base">
                    <div className="col-span-4 text-left">Product / Description</div>
                    <div className="col-span-2 text-center">Unit Price</div>
                    <div className="col-span-1 text-center">Qty</div>
                    <div className="col-span-1 text-center">Disc %</div>
                    <div className="col-span-2 text-right">Net Total</div>
                  </div>
                  
                  {/* Table Body - Map through lines for viewing */}
                  <div className="p-1">
                    {/* Assuming the `fields` array now holds the read-only data */}
                    {initialQuotation?.transactionLines?.length > 0 ? (
                      initialQuotation?.transactionLines.map((field, index) => {
                        // Watch for line data to display
                        // const line = watch(`transactionLines.${index}`);
                        
                   

                        return (
                          <div key={field.id} className="grid grid-cols-10 items-center px-3 py-2 border-b border-gray-600 hover:bg-gray-700 transition-colors duration-200 text-sm">
                            {/* Product / Description */}
                            <div className="col-span-4 text-left text-white font-medium pr-2">
                              {field.description || 'N/A'}
                            </div>
                            {/* Price */}
                          

                            <div className="col-span-2 text-center text-cyan-300">
                              {formatCurrency(field.unitPriceExclusive)}
                            </div>
                            {/* Quantity */}
                            <div className="col-span-1 text-center text-white">
                              {field?.quantity}
                            </div>
                            {/* Discount */}
                            <div className="col-span-1 text-center text-red-400">
                              {field?.discount_percentage}%
                            </div>
                            {/* Total (Net of Tax) */}
                            <div className="col-span-2 text-right text-white font-bold">
                              {formatCurrency(field.lineDiscountedPriceExclusive)}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="p-6 text-center text-cyan-400/70 text-base">No transaction lines on this quotation.</p>
                    )}
                  </div>
                </div>
              


              {/* Invoice Summary - View Only */}
            
                <div className="bg-gray-700/70 border border-cyan-500/30 p-4 rounded-xl shadow-lg">
                  <div className="font-extrabold text-xl sm:text-2xl text-cyan-200 mb-3 border-b border-cyan-500/50 pb-2">Summary</div>
                  <div className="space-y-2">
                    {/* Subtotal */}
                    <div className="flex justify-between items-center py-1 border-b border-gray-600">
                      <div className="text-base text-cyan-300">Subtotal (Exc Tax):</div>
                      <div className="text-base font-semibold text-white">{formatCurrency(initialQuotation.totalDiscountedPriceExclusive)}</div>
                    </div>
                    {/* Discount */}
                    {/* <div className="flex justify-between items-center py-1 border-b border-gray-600">
                      <div className="text-base text-cyan-300">Total Discount:</div>
                      <div className="text-base font-semibold text-red-400">{formatCurrency(initialQuotation.totalDiscountExclusive)}</div>
                    </div> */}
                    {/* Subtotal After Discount */}
                    {/* <div className="flex justify-between items-center py-1 border-b border-gray-600">
                      <div className="text-base text-cyan-300">Subtotal After Discount (Excl. Tax):</div>
                      <div className="text-base font-semibold text-white">{}</div>
                    </div> */}
                    {/* Tax */}
                    <div className="flex justify-between items-center py-1 border-b border-gray-600">
                      <div className="text-base text-cyan-300">Total Tax:</div>
                      <div className="text-base font-semibold text-white">{formatCurrency(initialQuotation.totalTaxAfterDiscounted)}</div>
                    </div>
                  </div>
                  {/* Grand Total */}
                  <div className="flex justify-between items-center pt-4 font-extrabold text-xl text-cyan-100">
                    <div className="text-lg">Grand Total (Incl. Tax):</div>
                    <div className="text-lg">{formatCurrency(initialQuotation.totalDiscountedPriceInclusive)}</div>
                  </div>
                  <div className="flex justify-between items-center pt-4 font-extrabold text-sm text-cyan-100">
                    <div className="text-sm">Total Discount:</div>
                    <div className="text-sm text-cyan-400">{formatCurrency(initialQuotation.totalDiscountInclusive)}</div>
                  </div>
                </div>
              
            </div>
          )}
          
          {/* Footer - Only a Back button is needed */}
          <div className="flex justify-center mt-8">
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-3 rounded-xl font-bold text-gray-900 bg-cyan-400 hover:bg-cyan-300 transition-all duration-300 shadow-xl tracking-wider uppercase"
            >
              Back to List
            </button>
          </div>

        </div>
      </div>
    </Layout>
  );
};

export default NewInvoice;
